import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  FaArrowLeft,
  FaBuilding,
  FaProjectDiagram,
  FaBoxOpen,
  FaCogs,
  FaCheckDouble,
  FaTruck,
  FaBalanceScale,
  FaRupeeSign,
  FaLayerGroup,
  FaFlask,
  FaPaperclip,
  FaFileSignature,
} from 'react-icons/fa';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import CommonService from '../../services/common.service';
import {
  completeP2pRfqLinkAfterModuleCreate,
  readP2pLaunchContextFromUrl,
} from '../../utils/p2pRfqLinkAfterCreate';
import StorageService from '../../services/StorageService';
import { API_URLS } from '../../constants/api-urls';
import { useFactoryDetailsMaster } from '../../hooks/useFactoryDetailsMaster';
import { useProductDetailsMaster } from '../../hooks/useProductDetailsMaster';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import {
  getInitialRfqForm,
  emptyProductRow,
  emptyTierRow,
  YES_NO,
  COMM_PREFS,
  CURRENCIES,
} from './contractManufacturerRfqFormState';
import { INDUSTRY_TYPE_MAP } from '../../constants/industryTypeMap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/contract_manufacturer_rfq_page.scss';

function mergeSavedPayload(base, payload) {
  if (!payload || typeof payload !== 'object') return base;
  const skip = new Set(['_meta', 'uploadedFiles']);
  const next = JSON.parse(JSON.stringify(base));
  for (const key of Object.keys(next)) {
    if (skip.has(key)) continue;
    const p = payload[key];
    if (p == null) continue;
    if (Array.isArray(p) && Array.isArray(next[key]) && p.length > 0) {
      next[key] = p;
    } else if (typeof p === 'object' && !Array.isArray(p) && typeof next[key] === 'object' && !Array.isArray(next[key])) {
      next[key] = { ...next[key], ...p };
    }
  }
  return next;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const MAX_FILE_BYTES = 4 * 1024 * 1024;

function isValidEmail(email) {
  const e = String(email || '').trim();
  return e.length > 3 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function isNumericOptional(v) {
  if (v === '' || v == null) return true;
  return !Number.isNaN(Number(String(v).replace(/,/g, '')));
}

function findOption(options, value) {
  if (!value || !Array.isArray(options) || options.length === 0) return null;
  const v = String(value).trim();
  if (!v) return null;
  return options.find((o) => String(o.value) === v || String(o.label) === v) || null;
}

function findMultiOptions(options, value) {
  if (!Array.isArray(options) || options.length === 0 || value == null) return [];
  const selectedValues = Array.isArray(value)
    ? value.map((v) => String(v).trim()).filter(Boolean)
    : String(value)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
  return options.filter((opt) => selectedValues.includes(String(opt.value)) || selectedValues.includes(String(opt.label)));
}

function extractApiErrorMessage(err, fallback) {
  const responseData = err?.response?.data;
  if (responseData?.message) return responseData.message;
  const fieldErrors = responseData?.fieldErrors;
  if (fieldErrors && typeof fieldErrors === 'object') {
    const firstKey = Object.keys(fieldErrors)[0];
    if (firstKey && fieldErrors[firstKey]) return String(fieldErrors[firstKey]);
  }
  return err?.message || fallback;
}

function toRequiredString(value) {
  if (value == null) return '';
  return String(value).trim();
}

function normalizeRfqPayloadForSubmit(payload) {
  const next = payload && typeof payload === 'object'
    ? JSON.parse(JSON.stringify(payload))
    : {};

  const products = Array.isArray(next.products) ? next.products : [];
  next.products = products.map((row) => ({
    ...row,
    partName: toRequiredString(row?.partName),
    uom: toRequiredString(row?.uom),
    quantity: toRequiredString(row?.quantity),
  }));

  next.commercial = {
    ...(next.commercial || {}),
    preferredPricingModel: toRequiredString(next?.commercial?.preferredPricingModel),
    paymentMethod: toRequiredString(next?.commercial?.paymentMethod),
    unitOfMeasurement: toRequiredString(next?.commercial?.unitOfMeasurement),
    paymentTerms: toRequiredString(next?.commercial?.paymentTerms),
    incoterms: toRequiredString(next?.commercial?.incoterms),
  };

  next.basePrice = {
    ...(next.basePrice || {}),
    price: toRequiredString(next?.basePrice?.price),
    moq: toRequiredString(next?.basePrice?.moq),
  };

  const sampleOffered =
    next?.sample?.sampleOffered === true ||
    String(next?.sample?.sampleOffered || '').toLowerCase() === 'true';
  if (sampleOffered) {
    next.sample = {
      ...(next.sample || {}),
      sampleType: toRequiredString(next?.sample?.sampleType),
      sampleCost: toRequiredString(next?.sample?.sampleCost),
      samplePrice: toRequiredString(next?.sample?.samplePrice),
      dispatchLeadTimeDays: toRequiredString(next?.sample?.dispatchLeadTimeDays),
      sampleShippingMode: toRequiredString(next?.sample?.sampleShippingMode),
      sampleReturnPolicy: toRequiredString(next?.sample?.sampleReturnPolicy),
    };
  }

  return next;
}

const mapSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  singleValue: (base) => ({ ...base, color: '#1f2937' }),
};

function YesNoSwitch({ checked, onToggle, id }) {
  return (
    <button
      type="button"
      id={id}
      className={`sp-switch ${checked ? 'on' : 'off'}`}
      role="switch"
      aria-checked={checked}
      onClick={() => onToggle(!checked)}
    >
      <span className="sp-switch-label">{checked ? 'Yes' : 'No'}</span>
      <span className="sp-switch-thumb" />
    </button>
  );
}

const FREQUENCY_OPTIONS = [
  { value: 'One-time', label: 'One-time' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Yearly', label: 'Yearly' },
];

const HEAT_TREATMENT_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'Annealing', label: 'Annealing' },
  { value: 'Normalising', label: 'Normalising' },
  { value: 'Quench & Temper', label: 'Quench & Temper' },
  { value: 'Carburising', label: 'Carburising' },
  { value: 'Carbonitriding', label: 'Carbonitriding' },
  { value: 'Nitriding', label: 'Nitriding' },
  { value: 'Induction/Flame Hardening', label: 'Induction/Flame Hardening' },
  { value: 'Solution Treat & Age', label: 'Solution Treat & Age' },
  { value: 'Stress Relieve', label: 'Stress Relieve' },
  { value: 'Vacuum Heat Treat', label: 'Vacuum Heat Treat' },
];

const COATING_FINISH_OPTIONS = [
  { value: 'Anodising II', label: 'Anodising II' },
  { value: 'Anodising III hard', label: 'Anodising III hard' },
  { value: 'Galvanising', label: 'Galvanising' },
  { value: 'Passivation', label: 'Passivation' },
  { value: 'Phosphating', label: 'Phosphating' },
  { value: 'Powder Coat', label: 'Powder Coat' },
  { value: 'E-coat', label: 'E-coat' },
  { value: 'Painting', label: 'Painting' },
  { value: 'PVD/CVD', label: 'PVD/CVD' },
  { value: 'Thermal Spray', label: 'Thermal Spray' },
  { value: 'Electroplating (Zn/Ni/Cr)', label: 'Electroplating (Zn/Ni/Cr)' },
];

const WELDING_OPTIONS = [
  { value: 'SMAW', label: 'SMAW' },
  { value: 'GMAW/MIG', label: 'GMAW/MIG' },
  { value: 'GTAW/TIG', label: 'GTAW/TIG' },
  { value: 'FCAW', label: 'FCAW' },
  { value: 'SAW', label: 'SAW' },
  { value: 'Spot/Projection', label: 'Spot/Projection' },
  { value: 'Laser', label: 'Laser' },
  { value: 'Brazing/Soldering', label: 'Brazing/Soldering' },
];

const NDT_OPTIONS = [
  { value: 'UT', label: 'UT' },
  { value: 'RT/X-ray', label: 'RT/X-ray' },
  { value: 'PT / DPI', label: 'PT / DPI' },
  { value: 'MP / MPI', label: 'MP / MPI' },
  { value: 'VT', label: 'VT' },
  { value: 'EDDy Current', label: 'EDDy Current' },
  { value: 'Leak Test', label: 'Leak Test' },
];

const CLEAN_ROOM_OPTIONS = [
  { value: 'ISO 5', label: 'ISO 5' },
  { value: 'ISO 6', label: 'ISO 6' },
  { value: 'ISO 7', label: 'ISO 7' },
  { value: 'ISO 8', label: 'ISO 8' },
];

const CERTIFICATE_REQUIRED_OPTIONS = [
  { value: 'ISO 9001', label: 'ISO 9001' },
  { value: 'IATF 16949', label: 'IATF 16949' },
  { value: 'AS9100', label: 'AS9100' },
  { value: 'ISO 13485', label: 'ISO 13485' },
  { value: 'ISO 22000/FSSC', label: 'ISO 22000/FSSC' },
  { value: 'GMP', label: 'GMP' },
  { value: 'HACCP', label: 'HACCP' },
  { value: 'RoHS', label: 'RoHS' },
  { value: 'REACH', label: 'REACH' },
  { value: 'CE', label: 'CE' },
  { value: 'UL', label: 'UL' },
  { value: 'IPC-A-610/WHMA', label: 'IPC-A-610/WHMA' },
];

const REGULATORY_CLAIMS_OPTIONS = [
  { value: 'RoHS (Restriction of Hazardous Substances)', label: 'RoHS (Restriction of Hazardous Substances)' },
  { value: 'REACH (Registration, Evaluation, Authorization and Restriction of Chemicals)', label: 'REACH (Registration, Evaluation, Authorization and Restriction of Chemicals)' },
  { value: 'WEEE (Waste Electrical and Electronic Equipment Directive)', label: 'WEEE (Waste Electrical and Electronic Equipment Directive)' },
  { value: 'Prop 65 (California Proposition 65)', label: 'Prop 65 (California Proposition 65)' },
  { value: 'Conflict Minerals (often via CMRT / RMI)', label: 'Conflict Minerals (often via CMRT / RMI)' },
  { value: 'Food-contact (US / EU / other jurisdictions)', label: 'Food-contact (US / EU / other jurisdictions)' },
  { value: 'Phthalate-free', label: 'Phthalate-free' },
  { value: 'BPA-free', label: 'BPA-free' },
  { value: 'Vegan', label: 'Vegan' },
  { value: 'Vegetarian', label: 'Vegetarian' },
  { value: 'Halal', label: 'Halal' },
  { value: 'Kosher', label: 'Kosher' },
  { value: 'Gluten-free', label: 'Gluten-free' },
  { value: 'Non-GMO (non-genetically modified organisms)', label: 'Non-GMO (non-genetically modified organisms)' },
  { value: 'Organic (US / EU / other standards)', label: 'Organic (US / EU / other standards)' },
  { value: 'Recyclable / Compostable packaging', label: 'Recyclable / Compostable packaging' },
  { value: 'FSSAI (India’s food regulatory claims / certifications)', label: 'FSSAI (India’s food regulatory claims / certifications)' },
];

const ACCEPTABLE_QUALITY_LEVEL_OPTIONS = [
  { value: 'Gen II @0.65', label: 'Gen II @0.65' },
  { value: 'Gen II @1.0', label: 'Gen II @1.0' },
  { value: 'Gen II @1.5', label: 'Gen II @1.5' },
  { value: 'Gen II @2.5', label: 'Gen II @2.5' },
  { value: 'Tight III @0.65', label: 'Tight III @0.65' },
];

const INSPECTION_REQUIRED_DOCUMENT_OPTIONS = [
  { value: 'CoC (Certificate of Conformity)', label: 'CoC (Certificate of Conformity)' },
  { value: 'CoA (Certificate of Analysis)', label: 'CoA (Certificate of Analysis)' },
  { value: 'MTC / MTR (Material Test Certificate / Report)', label: 'MTC / MTR (Material Test Certificate / Report)' },
  { value: 'Dimensional Inspection Report', label: 'Dimensional Inspection Report' },
  { value: 'FAI (First Article Inspection / AS9102)', label: 'FAI (First Article Inspection / AS9102)' },
  { value: 'PPAP L1-L5 (Production Part Approval Process)', label: 'PPAP L1-L5 (Production Part Approval Process)' },
  { value: 'PFMEA (Process Failure Mode & Effects Analysis)', label: 'PFMEA (Process Failure Mode & Effects Analysis)' },
  { value: 'Control Plan', label: 'Control Plan' },
  { value: 'PSW (Part Submission Warrant)', label: 'PSW (Part Submission Warrant)' },
  { value: 'Gauge R&R (Measurement System Analysis)', label: 'Gauge R&R (Measurement System Analysis)' },
  { value: 'RoHS / REACH Declaration', label: 'RoHS / REACH Declaration' },
  { value: 'WEEE Declaration', label: 'WEEE Declaration' },
  { value: 'MSDS / SDS (Material / Safety Data Sheet)', label: 'MSDS / SDS (Material / Safety Data Sheet)' },
  { value: 'NDT Report (UT / RT / MT / PT)', label: 'NDT Report (UT / RT / MT / PT)' },
  { value: 'WPS / PQR / WPQ (Welding Procedure / Qualification)', label: 'WPS / PQR / WPQ (Welding Procedure / Qualification)' },
  { value: 'Surface / Coating Reports', label: 'Surface / Coating Reports' },
  { value: 'Salt Spray Test Report (ASTM B117)', label: 'Salt Spray Test Report (ASTM B117)' },
  { value: 'Electrical Safety Test Report (UL / IEC)', label: 'Electrical Safety Test Report (UL / IEC)' },
  { value: 'Calibration Certificates', label: 'Calibration Certificates' },
  { value: 'Material Traceability Report / Batch Trace Report', label: 'Material Traceability Report / Batch Trace Report' },
];

const LABELLING_BARCODING_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'EAN/UPC/GS1', label: 'EAN/UPC/GS1' },
  { value: 'Batch/Lot & Mfg/Expiry', label: 'Batch/Lot & Mfg/Expiry' },
  { value: 'Serialisation/QR', label: 'Serialisation/QR' },
  { value: 'COO label', label: 'COO label' },
];

const TRANSPORT_MODE_OPTIONS = [
  { value: '', label: 'Preferred mode of Transport' },
  { value: 'Road', label: 'Road' },
  { value: 'Air', label: 'Air' },
  { value: 'Sea', label: 'Sea' },
  { value: 'Rail', label: 'Rail' },
  { value: 'Courier', label: 'Courier' },
];

const PRICING_MODEL_OPTIONS = [
  { value: 'Per Piece ( Unit Price )', label: 'Per Piece ( Unit Price )' },
  { value: 'Per Batch/Lot', label: 'Per Batch/Lot' },
  { value: 'PerHour ( Machine / Labour )', label: 'PerHour ( Machine / Labour )' },
  { value: 'Per Day / Per Shift', label: 'Per Day / Per Shift' },
  { value: 'Monthly Lease / Retainer', label: 'Monthly Lease / Retainer' },
  { value: 'Cost + ( BOM Pass Through + Markup )', label: 'Cost + ( BOM Pass Through + Markup )' },
  { value: 'Fixed project Fee', label: 'Fixed project Fee' },
];

const UNIT_OF_MEASUREMENT_OPTIONS = [
  { value: '', label: 'Unit of Measurement' },
  { value: 'Nos', label: 'Nos' },
  { value: 'Kg', label: 'Kg' },
  { value: 'Ton', label: 'Ton' },
  { value: 'Ltr', label: 'Ltr' },
  { value: 'Mtr', label: 'Mtr' },
];

const BATCH_LOT_UOM_OPTIONS = [
  { value: 'Batch', label: 'Batch' },
  { value: 'Lot', label: 'Lot' },
];

const PER_HOUR_UOM_OPTIONS = [
  { value: 'Machine Hour', label: 'Machine Hour' },
  { value: 'Labour Hour', label: 'Labour Hour' },
  { value: 'Line Hour', label: 'Line Hour' },
];

const PER_DAY_SHIFT_UOM_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'Shift', label: 'Shift' },
];

const PROJECT_RETAINER_UOM_OPTIONS = [
  { value: 'PCS', label: 'PCS' },
  { value: 'Set', label: 'Set' },
  { value: 'Pair', label: 'Pair' },
  { value: 'Kg', label: 'Kg' },
  { value: 'g', label: 'g' },
  { value: 'ton', label: 'ton' },
  { value: 'mm', label: 'mm' },
  { value: 'sq.m', label: 'sq.m' },
];

const PRICE_ESCALATION_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'CPI Index', label: 'CPI Index' },
  { value: 'LME ( Metals )', label: 'LME ( Metals )' },
  { value: 'Resin Index ( Chem/ Plastics)', label: 'Resin Index ( Chem/ Plastics)' },
  { value: 'Energy Surcharge', label: 'Energy Surcharge' },
  { value: 'FX Clause', label: 'FX Clause' },
];

const ADD_ON_CATEGORY_OPTIONS = [
  { value: 'Setup/Changeover per run', label: 'Setup/Changeover per run' },
  { value: 'Packaging materials', label: 'Packaging materials' },
  { value: 'QA/Inspection (FAI/PPAP)', label: 'QA/Inspection (FAI/PPAP)' },
  { value: 'Certification documents', label: 'Certification documents' },
  { value: 'Freight & Logistics', label: 'Freight & Logistics' },
  { value: 'Duties/Taxes', label: 'Duties/Taxes' },
  { value: 'Artwork/tool texturing', label: 'Artwork/tool texturing' },
  { value: 'Expedite/Rush fee', label: 'Expedite/Rush fee' },
  { value: 'Disposal/Waste handling', label: 'Disposal/Waste handling' },
];

const TOOLING_OWNERSHIP_OPTIONS = [
  { value: 'Buyer', label: 'Buyer' },
  { value: 'Supplier', label: 'Supplier' },
  { value: 'Shared', label: 'Shared' },
];

const JURISDICTION_OPTIONS = [
  { value: 'India', label: 'India' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'UAE', label: 'UAE' },
  { value: 'Uk', label: 'Uk' },
  { value: 'USA', label: 'USA' },
  { value: 'EU ( Specify)', label: 'EU ( Specify)' },
];

const DELIVERY_METHOD_OPTIONS = [
  { value: 'Direct Delivery', label: 'Direct Delivery' },
  { value: 'Warehouse Delivery', label: 'Warehouse Delivery' },
  { value: 'Pickup', label: 'Pickup' },
];

const WARRANTY_PERIOD_OPTIONS = [
  { value: 'Returnable', label: 'Returnable' },
  { value: 'Non- Returnable', label: 'Non- Returnable' },
  { value: 'On Approval Basis', label: 'On Approval Basis' },
];

const SAMPLE_TYPE_OPTIONS = [
  { value: 'Product Sample', label: 'Product Sample' },
  { value: 'Material Sample', label: 'Material Sample' },
  { value: 'Prototype', label: 'Prototype' },
  { value: 'Digital Mockup', label: 'Digital Mockup' },
];

const SAMPLE_COST_OPTIONS = [
  { value: 'Free', label: 'Free' },
  { value: 'Chargeable', label: 'Chargeable' },
];

const SAMPLE_SHIPPING_MODE_OPTIONS = [
  { value: 'Courier', label: 'Courier' },
  { value: 'Air', label: 'Air' },
  { value: 'Surface', label: 'Surface' },
  { value: 'Customer Pickup', label: 'Customer Pickup' },
];

const SAMPLE_RETURN_POLICY_OPTIONS = [
  { value: 'Returnable', label: 'Returnable' },
  { value: 'Non- Returnable', label: 'Non- Returnable' },
  { value: 'On Approval Basis', label: 'On Approval Basis' },
];

export default function ContractManufacturerRfqPage() {
  const { companyId: companyIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const p2pLaunch = readP2pLaunchContextFromUrl(searchParams);
  const idNum = companyIdParam != null && companyIdParam !== '' ? parseInt(companyIdParam, 10) : NaN;
  const idValid = Number.isFinite(idNum) && idNum > 0;
  const showManufacturerPicker = companyIdParam == null || companyIdParam === '';
  /** Send RFQ page without :companyId — broadcast to manufacturers matching Project → Industry type */
  const isBroadcastFlow = showManufacturerPicker;
  const [form, setForm] = useState(() => getInitialRfqForm());
  const [manufacturerName, setManufacturerName] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(() => idValid);
  const [loadError, setLoadError] = useState(null);
  const [declarationError, setDeclarationError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [broadcastConfirmOpen, setBroadcastConfirmOpen] = useState(false);
  const [broadcastMatchedCount, setBroadcastMatchedCount] = useState(null);
  const [broadcastPreviewLoading, setBroadcastPreviewLoading] = useState(false);
  const [broadcastSendLoading, setBroadcastSendLoading] = useState(false);
  const [savedFileUrls, setSavedFileUrls] = useState({});
  const submitLock = useRef(false);
  const { modeOfTransportOptions, shippingTermsOptions, paymentMethodOptions, paymentScheduleOptions } = useFactoryDetailsMaster();
  const { dimensionUnitOptions, attributeUnitOptions } = useProductDetailsMaster();
  const { currencyOptions } = useCurrencyMaster();
  const transportOptions = modeOfTransportOptions?.length
    ? modeOfTransportOptions
    : TRANSPORT_MODE_OPTIONS.filter((o) => o.value);
  const incotermOptions = shippingTermsOptions?.length
    ? shippingTermsOptions
    : [{ value: '', label: 'Incoterms' }];
  const paymentMethodMapped = paymentMethodOptions?.length
    ? paymentMethodOptions
    : [{ value: '', label: 'Payment method' }];
  const paymentTermsMapped = paymentScheduleOptions?.length
    ? paymentScheduleOptions
    : [{ value: '', label: 'Payment Terms' }];
  const unitOfMeasurementMapped = dimensionUnitOptions?.length
    ? dimensionUnitOptions
    : UNIT_OF_MEASUREMENT_OPTIONS.filter((o) => o.value);
  const unitOfMeasurementOptions = form.commercial.preferredPricingModel === 'Per Batch/Lot'
    ? BATCH_LOT_UOM_OPTIONS
    : (form.commercial.preferredPricingModel === 'PerHour ( Machine / Labour )'
      ? PER_HOUR_UOM_OPTIONS
      : (form.commercial.preferredPricingModel === 'Per Day / Per Shift'
        ? PER_DAY_SHIFT_UOM_OPTIONS
        : (['Monthly Lease / Retainer', 'Cost + ( BOM Pass Through + Markup )', 'Fixed project Fee'].includes(form.commercial.preferredPricingModel)
          ? PROJECT_RETAINER_UOM_OPTIONS
          : unitOfMeasurementMapped)));
  const productUomOptions = attributeUnitOptions?.length ? attributeUnitOptions : [];
  const currencyMappedOptions = currencyOptions?.length
    ? currencyOptions
    : CURRENCIES.map((c) => ({ value: c, label: c }));

  const setBiz = useCallback((k, v) => {
    setForm((f) => ({ ...f, business: { ...f.business, [k]: v } }));
  }, []);
  const setProj = useCallback((k, v) => {
    setForm((f) => ({ ...f, project: { ...f.project, [k]: v } }));
  }, []);
  const setTech = useCallback((k, v) => {
    setForm((f) => ({ ...f, technical: { ...f.technical, [k]: v } }));
  }, []);
  const setQual = useCallback((k, v) => {
    setForm((f) => ({ ...f, quality: { ...f.quality, [k]: v } }));
  }, []);
  const setPack = useCallback((k, v) => {
    setForm((f) => ({ ...f, packaging: { ...f.packaging, [k]: v } }));
  }, []);
  const setComm = useCallback((k, v) => {
    setForm((f) => ({ ...f, commercial: { ...f.commercial, [k]: v } }));
  }, []);
  const setBase = useCallback((k, v) => {
    setForm((f) => ({ ...f, basePrice: { ...f.basePrice, [k]: v } }));
  }, []);
  const setSamp = useCallback((k, v) => {
    setForm((f) => ({ ...f, sample: { ...f.sample, [k]: v } }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isBroadcastFlow) {
      setLoadError(null);
      setManufacturerName('Industry-matched manufacturers');
      setLoadingProfile(false);
      return undefined;
    }
    if (!idValid) {
      setLoadError(null);
      setManufacturerName('');
      setLoadingProfile(false);
      return undefined;
    }
    (async () => {
      setLoadingProfile(true);
      setLoadError(null);
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.PUBLIC_PROFILE(idNum));
        const data = res?.data ?? res;
        if (cancelled) return;
        setManufacturerName(data?.company_name || data?.companyName || 'Manufacturer');
        const fd = data?.facility_details || {};
        const contact = data?.contact_details || {};
        setForm((prev) => ({
          ...prev,
          business: {
            ...prev.business,
            companyName: prev.business.companyName || data?.company_name || '',
            contactName: prev.business.contactName || contact.contact_name || fd.contactName || '',
            email: prev.business.email || contact.contact_email || fd.contactEmail || '',
            phone: prev.business.phone || contact.contact_number || fd.contactPhone || fd.contact_phone || '',
          },
        }));
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.response?.data?.message || e?.message || 'Could not load manufacturer');
        }
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idNum, idValid, isBroadcastFlow]);

  useEffect(() => {
    let cancelled = false;
    const token = StorageService.getToken();
    if (!token || !idValid || loadingProfile || isBroadcastFlow) return undefined;
    (async () => {
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ(idNum));
        const draft = res?.data ?? res;
        if (cancelled || !draft?.payload) return;
        setForm((prev) => mergeSavedPayload(prev, draft.payload));
        if (draft.contactEmail) {
          setForm((prev) => ({
            ...prev,
            business: { ...prev.business, email: draft.contactEmail },
          }));
        }
        if (draft.payload?.uploadedFiles && typeof draft.payload.uploadedFiles === 'object') {
          setSavedFileUrls(draft.payload.uploadedFiles);
        }
      } catch {
        /* no draft or not logged in as buyer */
      }
    })();
    return () => { cancelled = true; };
  }, [idNum, idValid, loadingProfile, isBroadcastFlow]);

  useEffect(() => {
    const pricingModel = form.commercial.preferredPricingModel;
    const uom = form.commercial.unitOfMeasurement;
    if (!uom) return;

    const isBatchLot = pricingModel === 'Per Batch/Lot';
    const isPerHour = pricingModel === 'PerHour ( Machine / Labour )';
    const isPerDayShift = pricingModel === 'Per Day / Per Shift';
    const isProjectRetainerModel = ['Monthly Lease / Retainer', 'Cost + ( BOM Pass Through + Markup )', 'Fixed project Fee'].includes(pricingModel);

    const allowed = isBatchLot
      ? ['Batch', 'Lot']
      : (isPerHour
        ? ['Machine Hour', 'Labour Hour', 'Line Hour']
        : (isPerDayShift
          ? ['Day', 'Shift']
          : (isProjectRetainerModel ? ['PCS', 'Set', 'Pair', 'Kg', 'g', 'ton', 'mm', 'sq.m'] : null)));

    if (allowed && !allowed.includes(uom)) {
      setComm('unitOfMeasurement', '');
      return;
    }

    if (!allowed && ['Batch', 'Lot', 'Machine Hour', 'Labour Hour', 'Line Hour', 'Day', 'Shift', 'PCS', 'Set', 'Pair', 'Kg', 'g', 'ton', 'mm', 'sq.m'].includes(uom)) {
      setComm('unitOfMeasurement', '');
    }
  }, [form.commercial.preferredPricingModel]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateProductRow = (idx, key, value) => {
    setForm((f) => {
      const products = [...f.products];
      products[idx] = { ...products[idx], [key]: value };
      return { ...f, products };
    });
  };

  const addProductRow = () => setForm((f) => ({ ...f, products: [...f.products, emptyProductRow()] }));
  const removeProductRow = (idx) => setForm((f) => ({
    ...f,
    products: f.products.length > 1 ? f.products.filter((_, i) => i !== idx) : f.products,
  }));

  const updateTierRow = (idx, key, value) => {
    setForm((f) => {
      const tierBreaks = [...f.tierBreaks];
      tierBreaks[idx] = { ...tierBreaks[idx], [key]: value };
      return { ...f, tierBreaks };
    });
  };
  const addTier = () => setForm((f) => ({ ...f, tierBreaks: [...f.tierBreaks, emptyTierRow()] }));
  const removeTier = (idx) => {
    setForm((f) => {
      if (!Array.isArray(f.tierBreaks) || f.tierBreaks.length <= 1) return f;
      return { ...f, tierBreaks: f.tierBreaks.filter((_, i) => i !== idx) };
    });
  };

  const onTechFile = (slot, file) => {
    setForm((f) => ({
      ...f,
      technicalFiles: { ...f.technicalFiles, [slot]: file || null },
    }));
  };

  const onAttachFile = (slot, file) => {
    setForm((f) => ({
      ...f,
      attachmentsMeta: { ...f.attachmentsMeta, [slot]: file || null },
    }));
  };

  const validate = () => {
    const err = {};
    const req = (key, val, message) => {
      if (val == null) {
        err[key] = message;
        return;
      }
      const s = typeof val === 'string' ? val.trim() : val;
      if (s === '' || s === false) err[key] = message;
    };

    // Business / Project (required fields marked with *)
    req('business.contactName', form.business.contactName, 'Contact name is required');
    req('business.phone', form.business.phone, 'Phone is required');
    if (!isValidEmail(form.business.email)) err.email = 'Valid email is required';
    req('project.title', form.project.title, 'Project title is required');
    req('project.industryType', form.project.industryType, 'Industry type is required');
    req('project.expectedStartDate', form.project.expectedStartDate, 'Expected start date is required');

    // Products: require at least one complete line with name/uom/quantity
    let hasCompleteProduct = false;
    form.products.forEach((p, i) => {
      const anyFilled = [p.partName, p.partNumber, p.rev, p.uom, p.quantity, p.material].some((v) => String(v || '').trim() !== '');
      if (!anyFilled) return;
      if (!String(p.partName || '').trim()) err[`products.${i}.partName`] = 'Required';
      if (!String(p.uom || '').trim()) err[`products.${i}.uom`] = 'Required';
      if (!String(p.quantity || '').trim()) err[`products.${i}.quantity`] = 'Required';
      if (String(p.partName || '').trim() && String(p.uom || '').trim() && String(p.quantity || '').trim()) hasCompleteProduct = true;
    });
    if (!hasCompleteProduct) {
      err['products'] = 'Please add at least one product with Part Name, UOM, and Quantity';
    }

    // Commercial & Legal (required for submission)
    req('commercial.preferredPricingModel', form.commercial.preferredPricingModel, 'Preferred pricing model is required');
    req('commercial.paymentMethod', form.commercial.paymentMethod, 'Payment method is required');
    req('commercial.unitOfMeasurement', form.commercial.unitOfMeasurement, 'Unit of measurement is required');
    req('commercial.paymentTerms', form.commercial.paymentTerms, 'Payment terms are required');
    req('commercial.incoterms', form.commercial.incoterms, 'Incoterms are required');

    // Base Price (required for submission)
    req('basePrice.price', form.basePrice.price, 'Enter price is required');
    req('basePrice.moq', form.basePrice.moq, 'MOQ is required');

    // Sample details (only if sample offered)
    if (form.sample.sampleOffered) {
      req('sample.sampleType', form.sample.sampleType, 'Sample type is required');
      req('sample.sampleCost', form.sample.sampleCost, 'Sample cost is required');
      req('sample.samplePrice', form.sample.samplePrice, 'Sample price is required');
      req('sample.dispatchLeadTimeDays', form.sample.dispatchLeadTimeDays, 'Dispatch lead time is required');
      req('sample.sampleShippingMode', form.sample.sampleShippingMode, 'Sample shipping mode is required');
      req('sample.sampleReturnPolicy', form.sample.sampleReturnPolicy, 'Sample return policy is required');
    }

    if (!form.declarations.terms || !form.declarations.compliance) {
      setDeclarationError(true);
    } else {
      setDeclarationError(false);
    }
    const nums = [
      ['basePrice.moq', form.basePrice.moq],
      ['basePrice.price', form.basePrice.price],
      ['basePrice.cgst', form.basePrice.cgst],
      ['basePrice.sgst', form.basePrice.sgst],
      ['basePrice.igst', form.basePrice.igst],
      ['basePrice.cess', form.basePrice.cess],
      ['basePrice.vat', form.basePrice.vat],
    ];
    nums.forEach(([k, v]) => {
      if (!isNumericOptional(v)) err[k] = 'Enter a valid number';
    });
    form.tierBreaks.forEach((t, i) => {
      if (!isNumericOptional(t.minQuantity)) err[`tier.${i}.min`] = 'Invalid';
      if (!isNumericOptional(t.unitPrice)) err[`tier.${i}.price`] = 'Invalid';
    });
    setFieldErrors(err);
    const declOk = form.declarations.terms && form.declarations.compliance;
    return Object.keys(err).length === 0 && declOk;
  };

  const buildAttachments = async () => {
    const out = [];
    const pairs = [
      ['formulaRecipe', form.technicalFiles.formulaRecipe],
      ['pcbDocs', form.technicalFiles.pcbDocs],
      ['ingredientBom', form.technicalFiles.ingredientBom],
      ['nda', form.attachmentsMeta.nda],
      ['specification', form.attachmentsMeta.specification],
      ['packagingArtwork', form.attachmentsMeta.packagingArtwork],
      ['other', form.attachmentsMeta.other],
    ];
    for (const [slot, file] of pairs) {
      if (!file) continue;
      if (file.size > MAX_FILE_BYTES) {
        throw new Error(`File too large (max 4 MB): ${file.name}`);
      }
      const dataBase64 = await readFileAsDataUrl(file);
      out.push({ slot, fileName: file.name, dataBase64 });
    }
    return out;
  };

  const confirmBroadcastSend = async () => {
    if (broadcastSendLoading || submitLock.current) return;
    if (!validate()) {
      setBroadcastConfirmOpen(false);
      toast.error('Please fix the highlighted fields before sending request.');
      return;
    }
    submitLock.current = true;
    setBroadcastSendLoading(true);
    try {
      const attachments = await buildAttachments();
      const { declarations, attachmentsMeta, technicalFiles, ...rest } = form;
      const industryType = String(form.project.industryType || '').trim();
      const body = {
        industryTypes: industryType ? [industryType] : [],
        contactEmail: form.business.email.trim(),
        declarationTermsAccepted: true,
        declarationComplianceAccepted: true,
        form: normalizeRfqPayloadForSubmit(rest),
        attachments,
      };
      const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SUBMIT_RFQ_BROADCAST, body);
      const count = res?.data?.recipientCount;
      const msg =
        typeof count === 'number'
          ? `RFQ sent to ${count} manufacturer(s).`
          : res?.message || 'RFQ broadcast submitted.';
      toast.success(msg);
      setBroadcastConfirmOpen(false);
      setBroadcastMatchedCount(null);
      navigate('/contract-manufacturers/contract', { replace: true });
    } catch (err) {
      const apiFieldErrors = err?.response?.data?.fieldErrors;
      if (apiFieldErrors && typeof apiFieldErrors === 'object') {
        setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));
      }
      const m = extractApiErrorMessage(err, 'Submission failed');
      if (process.env.NODE_ENV !== 'production') {
        console.error('Broadcast RFQ submit failed:', err?.response?.data || err);
      }
      toast.error(m);
    } finally {
      setBroadcastSendLoading(false);
      submitLock.current = false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!idValid && !isBroadcastFlow) {
      toast.error('Invalid request.');
      return;
    }
    if (submitLock.current || isSubmitting || broadcastPreviewLoading || broadcastSendLoading) return;
    if (!validate()) {
      if (!form.declarations.terms || !form.declarations.compliance) {
        toast.error('Please accept both declarations before submitting.');
      } else {
        toast.error('Please fix the highlighted fields.');
      }
      return;
    }

    /** P2P CM: internal record only — never broadcast to manufacturers. */
    if (p2pLaunch?.p2p_transaction_id && isBroadcastFlow) {
      submitLock.current = true;
      setIsSubmitting(true);
      try {
        const attachments = await buildAttachments();
        const { declarations, attachmentsMeta, technicalFiles, ...rest } = form;
        const body = {
          contactEmail: form.business.email.trim(),
          declarationTermsAccepted: true,
          declarationComplianceAccepted: true,
          form: normalizeRfqPayloadForSubmit(rest),
          attachments,
          p2pContext: {
            p2p_transaction_id: p2pLaunch.p2p_transaction_id,
            p2p_organization_id: p2pLaunch.p2p_organization_id,
            source_context: 'p2p',
            category_id: p2pLaunch.category_id,
            estimated_amount: p2pLaunch.estimated_amount,
          },
        };
        const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SUBMIT_RFQ_P2P, body);
        const data = res?.data ?? res;
        const requiresApproval = !!(data?.requires_approval ?? data?.approval?.mode === 'pending');
        toast.success(
          requiresApproval
            ? 'RFQ submitted and sent for approval'
            : 'RFQ submitted successfully',
        );
        const cmRfqId = data?.id ?? data?.rfqId;
        if (cmRfqId) {
          const linked = await completeP2pRfqLinkAfterModuleCreate({
            searchParams,
            sourceModule: 'contract_manufacturing',
            sourceRfqId: cmRfqId,
            sourceRfqTitle: form?.project?.title || form?.project?.projectName || null,
            sourceRfqStatus: requiresApproval ? 'pending_approval' : 'submitted',
            navigate,
            p2pLaunchContext: p2pLaunch,
          });
          if (linked) return;
        }
        navigate(`/dashboard/p2p/transactions/${encodeURIComponent(p2pLaunch.p2p_transaction_id)}?tab=sourcing`, {
          replace: true,
        });
      } catch (err) {
        const apiFieldErrors = err?.response?.data?.fieldErrors;
        if (apiFieldErrors && typeof apiFieldErrors === 'object') {
          setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));
        }
        const m = err?.response?.data?.message || err?.message || 'Submission failed';
        toast.error(m);
      } finally {
        setIsSubmitting(false);
        submitLock.current = false;
      }
      return;
    }

    if (isBroadcastFlow) {
      submitLock.current = true;
      setBroadcastPreviewLoading(true);
      try {
        const industryType = String(form.project.industryType || '').trim();
        const previewBody = {
          industryTypes: industryType ? [industryType] : [],
          form: { project: { industryType: form.project.industryType } },
        };
        const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.PREVIEW_RFQ_BROADCAST, previewBody);
        const count = res?.data?.matchedCount;
        if (typeof count !== 'number' || count < 1) {
          toast.error(
            res?.message || 'No contract manufacturers match your industry type.'
          );
          return;
        }
        setBroadcastMatchedCount(count);
        setBroadcastConfirmOpen(true);
      } catch (err) {
        const m = extractApiErrorMessage(err, 'Could not check matches.');
        if (process.env.NODE_ENV !== 'production') {
          console.error('Broadcast RFQ preview failed:', err?.response?.data || err);
        }
        toast.error(m);
      } finally {
        setBroadcastPreviewLoading(false);
        submitLock.current = false;
      }
      return;
    }

    submitLock.current = true;
    setIsSubmitting(true);
    try {
      const attachments = await buildAttachments();
      const { declarations, attachmentsMeta, technicalFiles, ...rest } = form;
      const body = {
        contactEmail: form.business.email.trim(),
        declarationTermsAccepted: true,
        declarationComplianceAccepted: true,
        form: normalizeRfqPayloadForSubmit(rest),
        attachments,
      };

      if (p2pLaunch?.p2p_transaction_id) {
        const p2pBody = {
          ...body,
          p2pContext: {
            p2p_transaction_id: p2pLaunch.p2p_transaction_id,
            p2p_organization_id: p2pLaunch.p2p_organization_id,
            source_context: 'p2p',
            category_id: p2pLaunch.category_id,
            estimated_amount: p2pLaunch.estimated_amount,
          },
        };
        const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SUBMIT_RFQ_P2P, p2pBody);
        const data = res?.data ?? res;
        const requiresApproval = !!(data?.requires_approval ?? data?.approval?.mode === 'pending');
        toast.success(
          requiresApproval
            ? 'RFQ submitted and sent for approval'
            : 'RFQ submitted successfully',
        );
        const cmRfqId = data?.id ?? data?.rfqId;
        if (cmRfqId) {
          const linked = await completeP2pRfqLinkAfterModuleCreate({
            searchParams,
            sourceModule: 'contract_manufacturing',
            sourceRfqId: cmRfqId,
            sourceRfqTitle: form?.project?.title || form?.project?.projectName || null,
            sourceRfqStatus: requiresApproval ? 'pending_approval' : 'submitted',
            navigate,
            p2pLaunchContext: p2pLaunch,
          });
          if (linked) return;
        }
        navigate(`/dashboard/p2p/transactions/${encodeURIComponent(p2pLaunch.p2p_transaction_id)}?tab=sourcing`, {
          replace: true,
        });
        return;
      }

      const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SUBMIT_RFQ(idNum), body);
      const msg = res?.message || 'RFQ submitted successfully';
      toast.success(msg);
      const cmRfqId = res?.data?.id ?? res?.data?.rfqId;
      if (p2pLaunch?.p2p_transaction_id && cmRfqId) {
        const linked = await completeP2pRfqLinkAfterModuleCreate({
          searchParams,
          sourceModule: 'contract_manufacturing',
          sourceRfqId: cmRfqId,
          sourceRfqTitle: form?.project?.projectName || null,
          sourceRfqStatus: 'submitted',
          navigate,
        });
        if (linked) return;
      }
      navigate(`/contract-manufacturers/profile/${idNum}`);
    } catch (err) {
      const apiFieldErrors = err?.response?.data?.fieldErrors;
      if (apiFieldErrors && typeof apiFieldErrors === 'object') {
        setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));
      }
      const m = err?.response?.data?.message || err?.message || 'Submission failed';
      toast.error(m);
    } finally {
      setIsSubmitting(false);
      submitLock.current = false;
    }
  };

  const selectClass = 'form-select cm-rfq-control';
  const inputClass = 'form-control cm-rfq-control';

  if (!idValid && !isBroadcastFlow) {
    return (
      <div className="cm-rfq-page">
        <WebsiteTopBar />
        <div className="container py-5">
          <p className="text-danger">Invalid manufacturer link.</p>
          <Link to="/contract-manufacturers/rfq">Back to Send RFQ</Link>
          {' · '}
          <Link to="/contract-manufacturers/contract">Browse directory</Link>
        </div>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="cm-rfq-page">
        <WebsiteTopBar />
        <div className="container py-5 text-center text-muted">Loading…</div>
      </div>
    );
  }

  if (idValid && loadError) {
    return (
      <div className="cm-rfq-page">
        <WebsiteTopBar />
        <div className="container py-5">
          <p className="text-danger">{loadError}</p>
          <Link to="/contract-manufacturers/contract">Back to directory</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cm-rfq-page">
      <WebsiteTopBar />
      <div className="cm-rfq-inner">
        <div className="container py-3">
          <button
            type="button"
            className="cm-rfq-back btn btn-link text-decoration-none p-0 mb-2"
            onClick={() =>
              isBroadcastFlow
                ? navigate('/contract-manufacturers/rfq', { replace: true, state: {} })
                : navigate(-1)}
          >
            <FaArrowLeft className="me-1" />
            Back
          </button>
          <nav className="cm-rfq-breadcrumb small text-muted mb-3">
            <Link to="/home">Home</Link>
            <span className="mx-1">›</span>
            <Link to="/contract-manufacturers/contract">Contract Manufacturers</Link>
            <span className="mx-1">›</span>
            {isBroadcastFlow ? (
              <>
                <span className="text-muted">{manufacturerName}</span>
                <span className="mx-1">›</span>
              </>
            ) : (
              <>
                <Link to={`/contract-manufacturers/profile/${idNum}`}>{manufacturerName}</Link>
                <span className="mx-1">›</span>
              </>
            )}
            <span>Request For Quotation</span>
          </nav>
          <h1 className="cm-rfq-heading mb-4">Create Request for Quotation (RFQ)</h1>

          <form onSubmit={handleSubmit} noValidate>
            {/* Business */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaBuilding className="me-2" />Business Information</h2>
                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Company Name</label>
                    <input className={inputClass} value={form.business.companyName} onChange={(e) => setBiz('companyName', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Contact Name</label>
                    <input className={`${inputClass} ${fieldErrors['business.contactName'] ? 'is-invalid' : ''}`} value={form.business.contactName} onChange={(e) => setBiz('contactName', e.target.value)} />
                    {fieldErrors['business.contactName'] && <div className="invalid-feedback d-block">{fieldErrors['business.contactName']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Email *</label>
                    <input type="email" className={`${inputClass} ${fieldErrors.email ? 'is-invalid' : ''}`} value={form.business.email} onChange={(e) => setBiz('email', e.target.value)} required />
                    {fieldErrors.email && <div className="invalid-feedback d-block">{fieldErrors.email}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Phone</label>
                    <input className={`${inputClass} ${fieldErrors['business.phone'] ? 'is-invalid' : ''}`} value={form.business.phone} onChange={(e) => setBiz('phone', e.target.value)} />
                    {fieldErrors['business.phone'] && <div className="invalid-feedback d-block">{fieldErrors['business.phone']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Technical Contact</label>
                    <input className={inputClass} value={form.business.technicalContact} onChange={(e) => setBiz('technicalContact', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Commercial Contact</label>
                    <input className={inputClass} value={form.business.commercialContact} onChange={(e) => setBiz('commercialContact', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Preferred Communication</label>
                    <Select
                      className="input-pill p-0"
                      options={COMM_PREFS.filter((o) => o.value)}
                      value={findOption(COMM_PREFS, form.business.preferredCommunication)}
                      onChange={(selected) => setBiz('preferredCommunication', selected ? selected.value : '')}
                      placeholder="Select…"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Project */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaProjectDiagram className="me-2" />Project Overview</h2>
                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Project Title / Reference*</label>
                    <input className={`${inputClass} ${fieldErrors['project.title'] ? 'is-invalid' : ''}`} placeholder="Enter project title" value={form.project.title} onChange={(e) => setProj('title', e.target.value)} />
                    {fieldErrors['project.title'] && <div className="invalid-feedback d-block">{fieldErrors['project.title']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Industry Type*</label>
                    <Select
                      className={`input-pill p-0 ${fieldErrors['project.industryType'] ? 'is-invalid' : ''}`}
                      options={INDUSTRY_TYPE_MAP}
                      value={findOption(INDUSTRY_TYPE_MAP, form.project.industryType)}
                      onChange={(selected) => setProj('industryType', selected ? selected.value : '')}
                      placeholder="Select industry"
                      isClearable
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                    {fieldErrors['project.industryType'] && <div className="invalid-feedback d-block">{fieldErrors['project.industryType']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Manufacturing Process Families</label>
                    <input className={inputClass} placeholder="e.g., CNC, Injection Molding" value={form.project.manufacturingProcessFacilities} onChange={(e) => setProj('manufacturingProcessFacilities', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">NDA Required</label>
                    <div className="cm-rfq-switch mt-1">
                      <YesNoSwitch
                        checked={String(form.project.ndaRequired).toLowerCase() === 'yes'}
                        onToggle={(next) => setProj('ndaRequired', next ? 'yes' : 'no')}
                      />
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Expected Start Date*</label>
                    <input type="date" className={`${inputClass} ${fieldErrors['project.expectedStartDate'] ? 'is-invalid' : ''}`} value={form.project.expectedStartDate} onChange={(e) => setProj('expectedStartDate', e.target.value)} />
                    {fieldErrors['project.expectedStartDate'] && <div className="invalid-feedback d-block">{fieldErrors['project.expectedStartDate']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Frequency of Manufacturing</label>
                    <Select
                      className="input-pill p-0"
                      options={FREQUENCY_OPTIONS}
                      value={findOption(FREQUENCY_OPTIONS, form.project.frequencyOfManufacturing)}
                      onChange={(selected) => setProj('frequencyOfManufacturing', selected ? selected.value : '')}
                      placeholder="Select frequency"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Estimated Order Volume</label>
                    <input type="number" min="0" step="any" className={inputClass} placeholder="Enter volume" value={form.project.estimatedOrderVolume} onChange={(e) => setProj('estimatedOrderVolume', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Estimated Annual Usage</label>
                    <input type="number" min="0" step="any" className={inputClass} placeholder="Enter usage" value={form.project.estimatedAnnualUsage} onChange={(e) => setProj('estimatedAnnualUsage', e.target.value)} />
                  </div>
                  <div className="col-lg-6 col-md-6">
                    <label className="form-label">Life Time Volume</label>
                    <input type="number" min="0" step="any" className={inputClass} placeholder="Total lifetime volume" value={form.project.lifetimeVolume} onChange={(e) => setProj('lifetimeVolume', e.target.value)} />
                  </div>
                  <div className="col-lg-6 col-md-6">
                    <label className="form-label">Target Cost (Optional)</label>
                    <input type="number" min="0" step="any" className={inputClass} placeholder="Target cost per unit" value={form.project.targetCost} onChange={(e) => setProj('targetCost', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaBoxOpen className="me-2" />Products to be Manufactured</h2>
                <div className="table-responsive">
                  <table className="table table-bordered cm-rfq-table">
                    <thead>
                      <tr>
                        <th>Part / Item Name</th>
                        <th>Part #</th>
                        <th>Rev</th>
                        <th>UOM</th>
                        <th>Quantity</th>
                        <th className="cm-rfq-material-col">Material</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {form.products.map((row, idx) => (
                        <tr key={idx}>
                          {['partName', 'partNumber', 'rev', 'uom', 'quantity', 'material'].map((k) => (
                            <td key={k} className={k === 'material' ? 'cm-rfq-material-col' : ''}>
                              {k === 'uom' ? (
                                <Select
                                  className={`input-pill p-0 ${fieldErrors[`products.${idx}.uom`] ? 'is-invalid' : ''}`}
                                  options={productUomOptions}
                                  value={findOption(productUomOptions, row.uom)}
                                  onChange={(selected) => updateProductRow(idx, 'uom', selected ? selected.value : '')}
                                  placeholder="UOM"
                                  isClearable
                                  isSearchable={false}
                                  menuPosition="fixed"
                                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                  styles={mapSelectStyles}
                                />
                              ) : (
                                <input
                                  className={`form-control form-control-sm ${fieldErrors[`products.${idx}.${k}`] ? 'is-invalid' : ''}`}
                                  value={row[k]}
                                  onChange={(e) => updateProductRow(idx, k, e.target.value)}
                                />
                              )}
                            </td>
                          ))}
                          <td>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeProductRow(idx)} disabled={form.products.length < 2}>×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="btn cm-rfq-btn-secondary btn-sm" onClick={addProductRow}>+ Add row</button>
              </div>
            </div>

            {/* Technical */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaCogs className="me-2" />Technical Requirement</h2>
                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Materials Detail</label>
                    <input className={inputClass} placeholder="Material details" value={form.technical.materialsDetail} onChange={(e) => setTech('materialsDetail', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Heat Treatment</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={HEAT_TREATMENT_OPTIONS}
                      value={findMultiOptions(HEAT_TREATMENT_OPTIONS, form.technical.heatTreatment)}
                      onChange={(selected) => setTech('heatTreatment', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select treatment"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Coating / Surface Finish</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={COATING_FINISH_OPTIONS}
                      value={findMultiOptions(COATING_FINISH_OPTIONS, form.technical.coatingSurfaceFinish)}
                      onChange={(selected) => setTech('coatingSurfaceFinish', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select finish"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Hardness Target (HRO/RB/HV)</label>
                    <input className={inputClass} placeholder="e.g., HRC 45-50" value={form.technical.hardnessTarget} onChange={(e) => setTech('hardnessTarget', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Welding Requirement</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={WELDING_OPTIONS}
                      value={findMultiOptions(WELDING_OPTIONS, form.technical.weldingRequirement)}
                      onChange={(selected) => setTech('weldingRequirement', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select requirement"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">NDT (Type & Coverage)</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={NDT_OPTIONS}
                      value={findMultiOptions(NDT_OPTIONS, form.technical.ndt)}
                      onChange={(selected) => setTech('ndt', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select NDT"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Resin Polymer Grade</label>
                    <input className={inputClass} placeholder="e.g., ABS, PP, HDPE" value={form.technical.resinPolymerGrade} onChange={(e) => setTech('resinPolymerGrade', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Colour Code</label>
                    <input className={inputClass} placeholder="RAL/Pantone code" value={form.technical.colourCode} onChange={(e) => setTech('colourCode', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">PCB (Size-up & Finish)</label>
                    <input className={inputClass} placeholder="PCB specifications" value={form.technical.pcbStackupFinish} onChange={(e) => setTech('pcbStackupFinish', e.target.value)} />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Clean Room Class</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={CLEAN_ROOM_OPTIONS}
                      value={findMultiOptions(CLEAN_ROOM_OPTIONS, form.technical.cleanRoomClass)}
                      onChange={(selected) => setTech('cleanRoomClass', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select class"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Target Shelf/Life / Lifespan</label>
                    <input className={inputClass} placeholder="e.g., 24 months" value={form.technical.targetShelfLifeMonths} onChange={(e) => setTech('targetShelfLifeMonths', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Formula Recipe & Formula Provided</label>
                    <div className="cm-rfq-switch mt-1">
                      <YesNoSwitch
                        checked={
                          Array.isArray(form.technical.formulaRecipe)
                            ? form.technical.formulaRecipe.length > 0 && !form.technical.formulaRecipe.includes('No')
                            : String(form.technical.formulaRecipe || '').toLowerCase() === 'yes'
                        }
                        onToggle={(next) => setTech('formulaRecipe', next ? 'Yes' : 'No')}
                      />
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label d-block">c. Upload PCB (OCCO Gerber / BOM / ICU)</label>
                    <input type="file" className="form-control form-control-sm" onChange={(e) => onTechFile('pcbDocs', e.target.files?.[0] || null)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">cc. Inspection / PPAP List</label>
                    <input className={inputClass} placeholder="Inspection requirements" value={form.technical.ingredientBom || ''} onChange={(e) => setTech('ingredientBom', e.target.value)} />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Tooling / Module Screens</label>
                    <textarea className={inputClass} rows={2} placeholder="Tooling requirements" value={form.technical.toolingModuleDetails1} onChange={(e) => setTech('toolingModuleDetails1', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Tooling / Module Details</label>
                    <textarea className={inputClass} rows={2} placeholder="Detailed tooling specifications" value={form.technical.toolingModuleDetails2} onChange={(e) => setTech('toolingModuleDetails2', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Quality */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaCheckDouble className="me-2" />Quality & Compliance</h2>
                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Certificate Required</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={CERTIFICATE_REQUIRED_OPTIONS}
                      value={findMultiOptions(CERTIFICATE_REQUIRED_OPTIONS, form.quality.certificateRequired)}
                      onChange={(selected) => setQual('certificateRequired', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select certificate(s)"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Regulatory / Claims</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={REGULATORY_CLAIMS_OPTIONS}
                      value={findMultiOptions(REGULATORY_CLAIMS_OPTIONS, form.quality.regulatoryClaims)}
                      onChange={(selected) => setQual('regulatoryClaims', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select claim(s)"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Acceptable quality Level</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={ACCEPTABLE_QUALITY_LEVEL_OPTIONS}
                      value={findMultiOptions(ACCEPTABLE_QUALITY_LEVEL_OPTIONS, form.quality.acceptableQualityLevel)}
                      onChange={(selected) => setQual('acceptableQualityLevel', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select quality level(s)"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Inspection required Document</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={INSPECTION_REQUIRED_DOCUMENT_OPTIONS}
                      value={findMultiOptions(INSPECTION_REQUIRED_DOCUMENT_OPTIONS, form.quality.inspectionRequiredDocument)}
                      onChange={(selected) => setQual('inspectionRequiredDocument', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Select document(s)"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Test Methods / Standards</label>
                    <textarea className={inputClass} rows={3} value={form.quality.testMethodsStandards} onChange={(e) => setQual('testMethodsStandards', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Packaging */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaTruck className="me-2" />Packaging & Logistics</h2>
                <div className="row g-3">
                  <div className="col-lg-8 col-md-12">
                    <label className="form-label">Packaging Specification (Unit/inner/master...)</label>
                    <input className={inputClass} value={form.packaging.packagingSpecification} onChange={(e) => setPack('packagingSpecification', e.target.value)} />
                  </div>
                  <div className="col-lg-4 col-md-12">
                    <label className="form-label">Labelling & Barcoding</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={LABELLING_BARCODING_OPTIONS}
                      value={findMultiOptions(LABELLING_BARCODING_OPTIONS, form.packaging.labellingBarcoding)}
                      onChange={(selected) => setPack('labellingBarcoding', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Labelling & Barcoding"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Ship to Court / Postcode</label>
                    <input className={inputClass} value={form.packaging.shipToCountryPostcode} onChange={(e) => setPack('shipToCountryPostcode', e.target.value)} />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Preferred mode of Transport</label>
                    <Select
                      className="input-pill p-0"
                      options={transportOptions}
                      value={findOption(transportOptions, form.packaging.preferredTransport)}
                      onChange={(selected) => setPack('preferredTransport', selected ? selected.value : '')}
                      placeholder="Preferred mode of Transport"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-4 col-md-12">
                    <div className="cm-rfq-switch-row">
                      <label className="form-label mb-0" htmlFor="coldChain">Cold Chain Requirement</label>
                      <div className="cm-rfq-switch mb-0 d-flex align-items-center gap-2">
                        <YesNoSwitch id="coldChain" checked={!!form.packaging.coldChain} onToggle={(next) => setPack('coldChain', next)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Test Methods / Standards</label>
                    <textarea className={inputClass} rows={3} value={form.packaging.packagingTestMethods} onChange={(e) => setPack('packagingTestMethods', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Commercial */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaBalanceScale className="me-2" />Commercial & Legal</h2>
                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Preferred Pricing Model</label>
                    <Select
                      className={`input-pill p-0 ${fieldErrors['commercial.preferredPricingModel'] ? 'is-invalid' : ''}`}
                      options={PRICING_MODEL_OPTIONS}
                      value={findOption(PRICING_MODEL_OPTIONS, form.commercial.preferredPricingModel)}
                      onChange={(selected) => setComm('preferredPricingModel', selected ? selected.value : '')}
                      placeholder="Preferred Pricing Model"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                    {fieldErrors['commercial.preferredPricingModel'] && <div className="invalid-feedback d-block">{fieldErrors['commercial.preferredPricingModel']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Payment method</label>
                    <Select
                      className={`input-pill p-0 ${fieldErrors['commercial.paymentMethod'] ? 'is-invalid' : ''}`}
                      options={paymentMethodMapped}
                      value={findOption(paymentMethodMapped, form.commercial.paymentMethod)}
                      onChange={(selected) => setComm('paymentMethod', selected ? selected.value : '')}
                      placeholder="Payment method"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                    {fieldErrors['commercial.paymentMethod'] && <div className="invalid-feedback d-block">{fieldErrors['commercial.paymentMethod']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Unit of Measurement</label>
                    <Select
                      className={`input-pill p-0 ${fieldErrors['commercial.unitOfMeasurement'] ? 'is-invalid' : ''}`}
                      options={unitOfMeasurementOptions}
                      value={findOption(unitOfMeasurementOptions, form.commercial.unitOfMeasurement)}
                      onChange={(selected) => setComm('unitOfMeasurement', selected ? selected.value : '')}
                      placeholder="Unit of Measurement"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                    {fieldErrors['commercial.unitOfMeasurement'] && <div className="invalid-feedback d-block">{fieldErrors['commercial.unitOfMeasurement']}</div>}
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Payment Terms</label>
                    <Select
                      className={`input-pill p-0 ${fieldErrors['commercial.paymentTerms'] ? 'is-invalid' : ''}`}
                      options={paymentTermsMapped}
                      value={findOption(paymentTermsMapped, form.commercial.paymentTerms)}
                      onChange={(selected) => setComm('paymentTerms', selected ? selected.value : '')}
                      placeholder="Payment Terms"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                    {fieldErrors['commercial.paymentTerms'] && <div className="invalid-feedback d-block">{fieldErrors['commercial.paymentTerms']}</div>}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Price Validity Expectations( Days)</label>
                    <input type="number" min="0" step="1" className={inputClass} value={form.commercial.priceValidityDays} onChange={(e) => setComm('priceValidityDays', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Incoterms</label>
                    <Select
                      className={`input-pill p-0 ${fieldErrors['commercial.incoterms'] ? 'is-invalid' : ''}`}
                      options={incotermOptions}
                      value={findOption(incotermOptions, form.commercial.incoterms)}
                      onChange={(selected) => setComm('incoterms', selected ? selected.value : '')}
                      placeholder="Incoterms"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                    {fieldErrors['commercial.incoterms'] && <div className="invalid-feedback d-block">{fieldErrors['commercial.incoterms']}</div>}
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Price Escalation Basis</label>
                    <Select
                      className="input-pill p-0"
                      options={PRICE_ESCALATION_OPTIONS}
                      value={findOption(PRICE_ESCALATION_OPTIONS, form.commercial.priceEscalationClause)}
                      onChange={(selected) => setComm('priceEscalationClause', selected ? selected.value : '')}
                      placeholder="Price Escalation Basis"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Add on Categories</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      options={ADD_ON_CATEGORY_OPTIONS}
                      value={findMultiOptions(ADD_ON_CATEGORY_OPTIONS, form.commercial.addOnCategories)}
                      onChange={(selected) => setComm('addOnCategories', selected ? selected.map((s) => s.value) : [])}
                      placeholder="Add on Categories"
                      isClearable
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <div className="cm-rfq-switch-row">
                      <label className="form-label mb-0" htmlFor="priceNeg">Is price negotiable ?</label>
                      <div className="cm-rfq-switch mb-0 d-flex align-items-center gap-2">
                        <YesNoSwitch id="priceNeg" checked={!!form.commercial.priceNegotiable} onToggle={(next) => setComm('priceNegotiable', next)} />
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Liquidated Damage / Delay Penalty</label>
                    <input className={inputClass} value={form.commercial.liquidatedDamages} onChange={(e) => setComm('liquidatedDamages', e.target.value)} />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <div className="cm-rfq-switch-row">
                      <label className="form-label mb-0" htmlFor="toolNre">Is Tooling / NRE Possible ?</label>
                      <div className="cm-rfq-switch mb-0 d-flex align-items-center gap-2">
                        <YesNoSwitch id="toolNre" checked={!!form.commercial.toolingNrePossible} onToggle={(next) => setComm('toolingNrePossible', next)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Tooling IME</label>
                    <input className={inputClass} value={form.commercial.toolingNre} onChange={(e) => setComm('toolingNre', e.target.value)} />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Tooling Ownership</label>
                    <Select
                      className="input-pill p-0"
                      options={TOOLING_OWNERSHIP_OPTIONS}
                      value={findOption(TOOLING_OWNERSHIP_OPTIONS, form.commercial.toolingOwnership)}
                      onChange={(selected) => setComm('toolingOwnership', selected ? selected.value : '')}
                      placeholder="Tooling Ownership"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Jurisdiction / Governing Law</label>
                    <Select
                      className="input-pill p-0"
                      options={JURISDICTION_OPTIONS}
                      value={findOption(JURISDICTION_OPTIONS, form.commercial.jurisdiction)}
                      onChange={(selected) => setComm('jurisdiction', selected ? selected.value : '')}
                      placeholder="Jurisdiction / Governing Law"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">Delivery Methods</label>
                    <Select
                      className="input-pill p-0"
                      options={DELIVERY_METHOD_OPTIONS}
                      value={findOption(DELIVERY_METHOD_OPTIONS, form.commercial.deliveryMethods)}
                      onChange={(selected) => setComm('deliveryMethods', selected ? selected.value : '')}
                      placeholder="Delivery Methods"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Port Of Shipping</label>
                    <input className={inputClass} value={form.commercial.portOfShipping} onChange={(e) => setComm('portOfShipping', e.target.value)} />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Expected Dispatch Date</label>
                    <input type="date" className={inputClass} value={form.commercial.expectedDispatchDate} onChange={(e) => setComm('expectedDispatchDate', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Base price */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaRupeeSign className="me-2" />Base Price</h2>
                <div className="cm-rfq-base-grid">
                  <div>
                    <label className="form-label">Enter Price $</label>
                    <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors['basePrice.price'] ? 'is-invalid' : ''}`} placeholder="0.0" value={form.basePrice.price} onChange={(e) => setBase('price', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">MOQ</label>
                    <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors['basePrice.moq'] ? 'is-invalid' : ''}`} placeholder="Min qty" value={form.basePrice.moq} onChange={(e) => setBase('moq', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">COST %</label>
                    <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors['basePrice.cess'] ? 'is-invalid' : ''}`} placeholder="0" value={form.basePrice.cess} onChange={(e) => setBase('cess', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">SGST %</label>
                    <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors['basePrice.sgst'] ? 'is-invalid' : ''}`} placeholder="0" value={form.basePrice.sgst} onChange={(e) => setBase('sgst', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">IGST %</label>
                    <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors['basePrice.igst'] ? 'is-invalid' : ''}`} placeholder="0" value={form.basePrice.igst} onChange={(e) => setBase('igst', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">CGSS %</label>
                    <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors['basePrice.cgst'] ? 'is-invalid' : ''}`} placeholder="0" value={form.basePrice.cgst} onChange={(e) => setBase('cgst', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">VAT %</label>
                    <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors['basePrice.vat'] ? 'is-invalid' : ''}`} placeholder="0" value={form.basePrice.vat} onChange={(e) => setBase('vat', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Other Taxable</label>
                    <input className={inputClass} placeholder="Tax name" value={form.basePrice.otherTaxes} onChange={(e) => setBase('otherTaxes', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Tiers */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <div className="cm-rfq-tier-head">
                  <h2 className="cm-rfq-section-title mb-0"><FaLayerGroup className="me-2" />Preferred Tier breaks</h2>
                  <button type="button" className="btn btn-sm cm-rfq-tier-add-btn" onClick={addTier}>+ Add</button>
                </div>
                {form.tierBreaks.map((t, idx) => (
                  <div className="row g-2 align-items-end mb-2" key={idx}>
                    <div className="col-lg-2 col-md-6">
                      <label className="form-label">Tier</label>
                      <input className={inputClass} placeholder="Tier 2" value={t.tierLabel} onChange={(e) => updateTierRow(idx, 'tierLabel', e.target.value)} />
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <label className="form-label">Min Quantity</label>
                      <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors[`tier.${idx}.min`] ? 'is-invalid' : ''}`} placeholder="Min qty" value={t.minQuantity} onChange={(e) => updateTierRow(idx, 'minQuantity', e.target.value)} />
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <label className="form-label">Tier Unit Price</label>
                      <input type="number" min="0" step="any" className={`${inputClass} ${fieldErrors[`tier.${idx}.price`] ? 'is-invalid' : ''}`} placeholder="Price" value={t.unitPrice} onChange={(e) => updateTierRow(idx, 'unitPrice', e.target.value)} />
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <label className="form-label">Currency</label>
                      <Select
                        className="input-pill p-0"
                        options={currencyMappedOptions}
                        value={findOption(currencyMappedOptions, t.currency)}
                        onChange={(selected) => updateTierRow(idx, 'currency', selected ? selected.value : '')}
                        placeholder="Currency"
                        isClearable
                        isSearchable={false}
                        menuPosition="fixed"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={mapSelectStyles}
                      />
                    </div>
                    <div className="col-lg-3 col-md-10">
                      <label className="form-label">Remarks If Any</label>
                      <input className={inputClass} placeholder="Remarks" value={t.remarks} onChange={(e) => updateTierRow(idx, 'remarks', e.target.value)} />
                    </div>
                    <div className="col-lg-1 col-md-2 d-flex align-items-end justify-content-center">
                      <button
                        type="button"
                        className="btn cm-rfq-tier-remove"
                        onClick={() => removeTier(idx)}
                        disabled={form.tierBreaks.length <= 1}
                        aria-label="Remove tier row"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sample */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaFlask className="me-2" />Sample Details</h2>
                <div className="row g-3">
                  <div className="col-md-3 d-flex align-items-center">
                    <div className="cm-rfq-switch d-flex align-items-center gap-2">
                      <label className="form-label mb-0" htmlFor="sOff">Sample Offered</label>
                      <YesNoSwitch id="sOff" checked={!!form.sample.sampleOffered} onToggle={(next) => setSamp('sampleOffered', next)} />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Sample Type</label>
                    <Select
                      className="input-pill p-0"
                      options={SAMPLE_TYPE_OPTIONS}
                      value={findOption(SAMPLE_TYPE_OPTIONS, form.sample.sampleType)}
                      onChange={(selected) => setSamp('sampleType', selected ? selected.value : '')}
                      placeholder="Sample Type"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Sample Cost</label>
                    <Select
                      className="input-pill p-0"
                      options={SAMPLE_COST_OPTIONS}
                      value={findOption(SAMPLE_COST_OPTIONS, form.sample.sampleCost)}
                      onChange={(selected) => setSamp('sampleCost', selected ? selected.value : '')}
                      placeholder="Sample Cost"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Price</label>
                    <input type="number" min="0" step="any" className={inputClass} value={form.sample.samplePrice} onChange={(e) => setSamp('samplePrice', e.target.value)} />
                  </div>
                  <div className="col-md-1">
                    <label className="form-label">Cur.</label>
                    <Select
                      className="input-pill p-0"
                      options={currencyMappedOptions}
                      value={findOption(currencyMappedOptions, form.sample.sampleCurrency)}
                      onChange={(selected) => setSamp('sampleCurrency', selected ? selected.value : '')}
                      placeholder="Currency"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Dispatch Lead time (Days)</label>
                    <input type="number" min="0" step="1" className={inputClass} value={form.sample.dispatchLeadTimeDays} onChange={(e) => setSamp('dispatchLeadTimeDays', e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Sample Shipping Mode</label>
                    <Select
                      className="input-pill p-0"
                      options={SAMPLE_SHIPPING_MODE_OPTIONS}
                      value={findOption(SAMPLE_SHIPPING_MODE_OPTIONS, form.sample.sampleShippingMode)}
                      onChange={(selected) => setSamp('sampleShippingMode', selected ? selected.value : '')}
                      placeholder="Sample Shipping Mode"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Sample return Policy</label>
                    <Select
                      className="input-pill p-0"
                      options={SAMPLE_RETURN_POLICY_OPTIONS}
                      value={findOption(SAMPLE_RETURN_POLICY_OPTIONS, form.sample.sampleReturnPolicy)}
                      onChange={(selected) => setSamp('sampleReturnPolicy', selected ? selected.value : '')}
                      placeholder="Sample return Policy"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Remarks / Instructions</label>
                    <textarea className={inputClass} rows={2} value={form.sample.sampleRemarks} onChange={(e) => setSamp('sampleRemarks', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Warranty & Guarantee */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaCheckDouble className="me-2" />Warraty & Guarantee</h2>
                <div className="row g-3">
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Warranty</label>
                    <input className={inputClass} placeholder="e.g. 12 months" value={form.sample.warranty} onChange={(e) => setSamp('warranty', e.target.value)} />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Period of Warranty</label>
                    <Select
                      className="input-pill p-0"
                      options={WARRANTY_PERIOD_OPTIONS}
                      value={findOption(WARRANTY_PERIOD_OPTIONS, form.sample.warrantyPeriod)}
                      onChange={(selected) => setSamp('warrantyPeriod', selected ? selected.value : '')}
                      placeholder="Select period"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Remarks</label>
                    <input className={inputClass} placeholder="Warranty remarks" value={form.sample.warrantyRemarks} onChange={(e) => setSamp('warrantyRemarks', e.target.value)} />
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Guarantee</label>
                    <input className={inputClass} placeholder="Guarantee details" value={form.sample.guarantee} onChange={(e) => setSamp('guarantee', e.target.value)} />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Period of Guarantee</label>
                    <Select
                      className="input-pill p-0"
                      options={WARRANTY_PERIOD_OPTIONS}
                      value={findOption(WARRANTY_PERIOD_OPTIONS, form.sample.guaranteePeriod)}
                      onChange={(selected) => setSamp('guaranteePeriod', selected ? selected.value : '')}
                      placeholder="Select period"
                      isClearable
                      isSearchable={false}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={mapSelectStyles}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">Remarks</label>
                    <input className={inputClass} placeholder="Guarantee remarks" value={form.sample.guaranteeRemarks} onChange={(e) => setSamp('guaranteeRemarks', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="cm-rfq-section card mb-3">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaPaperclip className="me-2" />Attachment</h2>
                <div className="row g-3">
                  {[
                    ['nda', 'Upload NDA'],
                    ['specification', 'Specification Sheet / SOW'],
                    ['packagingArtwork', 'Packaging Artwork / Dieline'],
                    ['other', 'Other Attachments'],
                  ].map(([slot, label]) => (
                    <div className="col-md-6" key={slot}>
                      <label className="form-label">{label}</label>
                      <input type="file" className="form-control form-control-sm" onChange={(e) => onAttachFile(slot, e.target.files?.[0] || null)} />
                      {savedFileUrls[slot] && (
                        <div className="small text-success mt-1">Previously uploaded: {savedFileUrls[slot]}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Declaration */}
            <div className="cm-rfq-section card mb-3 cm-rfq-declaration-card">
              <div className="card-body">
                <h2 className="cm-rfq-section-title"><FaFileSignature className="me-2" />Declaration</h2>

                <div className="cm-rfq-declaration-copy">
                  <div className="cm-rfq-declaration-copy-title">Declaration:</div>
                  <div className="cm-rfq-declaration-copy-text">
                    We hereby confirm that all information, Pricing, Technical details in the RFQ submission details are Accurate, Complete and in Accordance with Applicable laws, industry Standards and Tax regulations.
                    We understand that any false or misleading information may result in disqualification or legal Action.
                  </div>
                </div>

                <div className="cm-rfq-declaration-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="decTerms"
                    checked={form.declarations.terms}
                    onChange={(e) => setForm((f) => ({ ...f, declarations: { ...f.declarations, terms: e.target.checked } }))}
                  />
                  <div className="cm-rfq-declaration-check-body">
                    <label className="cm-rfq-declaration-check-title" htmlFor="decTerms">
                      I agree to the Terms & Conditions and Platform Policy
                    </label>
                    <div className="cm-rfq-declaration-check-sub">
                      By checking this box, you acknowledge that you have read and agree to abide by all terms and conditions.
                    </div>
                  </div>
                </div>

                <div className="cm-rfq-declaration-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="decComp"
                    checked={form.declarations.compliance}
                    onChange={(e) => setForm((f) => ({ ...f, declarations: { ...f.declarations, compliance: e.target.checked } }))}
                  />
                  <div className="cm-rfq-declaration-check-body">
                    <label className="cm-rfq-declaration-check-title" htmlFor="decComp">
                      I confirm product Compliance And Manufacturing Capabilities
                    </label>
                    <div className="cm-rfq-declaration-check-sub">
                      You confirm that all products meet the specified quality standards and comply with country of origin declarations.
                    </div>
                  </div>
                </div>

                {(declarationError && (!form.declarations.terms || !form.declarations.compliance)) && (
                  <div className="cm-rfq-declaration-bar" role="alert">
                    Both declarations must be confirmed before submitting your RFQ Response
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex gap-2 mb-4 justify-content-end">
              <button
                type="submit"
                className="btn cm-rfq-btn-primary px-4"
                disabled={isSubmitting || broadcastPreviewLoading || broadcastSendLoading}
              >
                {isBroadcastFlow
                  ? (broadcastPreviewLoading ? 'Checking matches…' : 'Submit RFQ Response')
                  : (isSubmitting ? 'Submitting…' : 'Submit RFQ Response')}
              </button>
              <Link
                to={isBroadcastFlow ? '/contract-manufacturers/contract' : `/contract-manufacturers/profile/${idNum}`}
                className="btn btn-outline-secondary"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>

      {isBroadcastFlow && broadcastConfirmOpen && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cm-broadcast-confirm-title"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="cm-broadcast-confirm-title">
                  Broadcast RFQ
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  disabled={broadcastSendLoading}
                  onClick={() => {
                    if (!broadcastSendLoading) {
                      setBroadcastConfirmOpen(false);
                      setBroadcastMatchedCount(null);
                    }
                  }}
                />
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Based on the industry type in your RFQ,{' '}
                  <strong>{broadcastMatchedCount}</strong>{' '}
                  contract manufacturer{broadcastMatchedCount !== 1 ? 's' : ''}{' '}
                  {broadcastMatchedCount === 1 ? 'matches' : 'match'} your request and will receive this RFQ after you click Send Request.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={broadcastSendLoading}
                  onClick={() => {
                    setBroadcastConfirmOpen(false);
                    setBroadcastMatchedCount(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={broadcastSendLoading}
                  onClick={confirmBroadcastSend}
                >
                  {broadcastSendLoading ? 'Sending…' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
